import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Wifi, LogIn, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { makeT } from "@/lib/labI18n";

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext(null);
export const useLabAuth = () => useContext(Ctx);

export function fmtErr(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function LabAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      setUser(r.ok ? await r.json() : false);
    } catch { setUser(false); }
    setLoading(false);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const logout = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch { /* noop */ }
    setUser(false);
  };
  return <Ctx.Provider value={{ user, loading, refresh, logout }}>{children}</Ctx.Provider>;
}

export function LabGuard({ children }) {
  const { user, loading } = useLabAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-[#0B1B33] text-cyan-300"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/lab/login" replace />;
  return children;
}

function Shell({ icon, title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0B1B33] grid place-items-center px-4" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(8,145,178,0.18), transparent 40%)" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-white mb-6">
          <span className="w-10 h-10 rounded-xl bg-cyan-500/20 border border-cyan-400/40 grid place-items-center text-cyan-300">{icon}</span>
          <div>
            <div className="text-xl font-extrabold tracking-wide">NEXIS</div>
            <div className="text-[11px] text-cyan-300/80 uppercase tracking-widest">Connectivity Infrastructure</div>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 shadow-2xl">
          <h1 className="text-lg font-bold text-white">{title}</h1>
          <p className="text-xs text-cyan-200/70 mb-5">{subtitle}</p>
          {children}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-5">BSGOTECH × TecNon · São Tomé e Príncipe</p>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-white/15 bg-white/5 text-white placeholder-slate-400 px-3 py-2.5 text-sm outline-none focus:border-cyan-400";

export function LabLogin() {
  const lang = (typeof localStorage !== "undefined" && localStorage.getItem("nexis_lang")) || "pt";
  const t = makeT(lang);
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ email, password }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(fmtErr(d.detail, t("auth.err")));
      nav("/lab");
    } catch (e2) { setErr(e2.message || t("auth.err")); } finally { setBusy(false); }
  };
  return (
    <Shell icon={<Wifi size={18} />} title={t("auth.login_title")} subtitle={t("auth.subtitle")}>
      <form onSubmit={submit} className="space-y-3" data-testid="lab-login-form">
        <input data-testid="login-email" type="email" required placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="login-password" type="password" required placeholder={t("auth.password")} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        {err && <p data-testid="login-error" className="text-xs text-red-300 bg-red-500/10 border border-red-400/30 rounded-md px-3 py-2">{err}</p>}
        <button data-testid="login-submit" disabled={busy} className="w-full flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#06243f] font-bold py-2.5 text-sm disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} {busy ? t("auth.signing") : t("auth.signin")}
        </button>
      </form>
    </Shell>
  );
}

export function LabSetPassword() {
  const lang = (typeof localStorage !== "undefined" && localStorage.getItem("nexis_lang")) || "pt";
  const t = makeT(lang);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/invite/${token}`);
        if (!r.ok) throw new Error();
        setInfo(await r.json());
      } catch { setInfo(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) return setErr(t("auth.min8"));
    if (pw !== pw2) return setErr(t("auth.mismatch"));
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/set-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, password: pw }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(fmtErr(d.detail, t("auth.invalid_link")));
      setDone(true); setTimeout(() => nav("/lab/login"), 1800);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  return (
    <Shell icon={<KeyRound size={18} />} title={t("auth.set_title")} subtitle={info && info.email ? info.email : t("auth.subtitle")}>
      {info === false ? (
        <p data-testid="setpw-invalid" className="text-sm text-red-300">{t("auth.invalid_link")}</p>
      ) : done ? (
        <p data-testid="setpw-done" className="text-sm text-green-300">{t("auth.set_done")}</p>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="setpw-form">
          <input data-testid="setpw-pw" type="password" required placeholder={t("auth.new_password")} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} />
          <input data-testid="setpw-pw2" type="password" required placeholder={t("auth.confirm_password")} value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} />
          {err && <p data-testid="setpw-error" className="text-xs text-red-300 bg-red-500/10 border border-red-400/30 rounded-md px-3 py-2">{err}</p>}
          <button data-testid="setpw-submit" disabled={busy} className="w-full flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#06243f] font-bold py-2.5 text-sm disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} {t("auth.save")}
          </button>
        </form>
      )}
    </Shell>
  );
}
